import React, { useMemo } from 'react';
import { Typography, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Vehicle, VehicleWithDriver } from '../../../models/vehicle';
import { getCountryDisplay } from '../../../utils/countryUtils';

interface VehicleCard2Props {
    vehicle: Vehicle | VehicleWithDriver;
    onClick: (vehicle: Vehicle | VehicleWithDriver) => void;
}

const VehicleCard2: React.FC<VehicleCard2Props> = ({ vehicle, onClick }) => {
  const { t } = useTranslation();

  const getVehicleStatus = (vehicle: Vehicle | VehicleWithDriver) => {
    if (!vehicle.isActive) return 'inactive';
    if (vehicle.emptyAt) {
      const emptyDate = new Date(vehicle.emptyAt);
      const now = new Date();
      if (emptyDate < now) return 'delayed';
    }
    return 'active';
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return t('On route');
      case 'delayed':
        return t('Waiting');
      case 'inactive':
        return t('Inactive');
      default:
        return t('Unknown');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return '#10b981'; // green
      case 'delayed':
        return '#f59e0b'; // orange
      case 'inactive':
        return '#9E9E9E'; // grey
      default:
        return '#9E9E9E';
    }
  };

  // Calculate distance from currentBooking.distanceKms
  const calculateDistance = () => {
    // Use distanceKms from currentBooking if available and not null
    const distanceKms = (vehicle as any).currentBooking?.distanceKms;
    if (distanceKms !== null && distanceKms !== undefined && typeof distanceKms === 'number') {
      return `${distanceKms.toLocaleString()} km`;
    }
    // Fallback to N/A if no distance available
    return t('N/A');
  };

  // Calculate estimated time or empty time from emptyAt
  const getTimeInfo = () => {
    if (!vehicle.emptyAt) {
      return {
        label: t('Estimated time'),
        value: t('N/A'),
        isPast: false
      };
    }

    const emptyDate = new Date(vehicle.emptyAt);
    const now = new Date();
    const diffMs = emptyDate.getTime() - now.getTime();

    // If the date is in the past
    if (diffMs < 0) {
      const totalMinutes = Math.floor(Math.abs(diffMs) / (1000 * 60));
      const days = Math.floor(totalMinutes / (60 * 24));
      const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
      const minutes = totalMinutes % 60;

      let timeString = '';
      if (days > 0) {
        timeString = `${days} d ${hours} h ${minutes} min`;
      } else if (hours > 0) {
        timeString = `${hours} h ${minutes} min`;
      } else {
        timeString = `${minutes} min`;
      }

      return {
        label: t('Empty time'),
        value: timeString,
        isPast: true
      };
    }

    // Calculate days, hours and minutes remaining
    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    let timeString = '';
    if (days > 0) {
      timeString = `${days}d ${hours}h ${minutes} min`;
    } else if (hours > 0) {
      timeString = `${hours} h ${minutes} min`;
    } else {
      timeString = `${minutes} min`;
    }

    return {
      label: t('Estimated time'),
      value: timeString,
      isPast: false
    };
  };

  const status = getVehicleStatus(vehicle);
  const statusColor = getStatusColor(status);
  const statusLabel = getStatusLabel(status);
  const distance = calculateDistance();
  const timeInfo = getTimeInfo();

  // Get route information from currentBooking
  const routeInfo = useMemo(() => {
    const currentBooking = (vehicle as any).currentBooking;
    if (!currentBooking || !currentBooking.loadingPlaces || currentBooking.loadingPlaces.length === 0) {
      return null;
    }

    const loadingPlace = currentBooking.loadingPlaces.find((lp: any) => lp.loadingType === 'LOADING');
    const unloadingPlace = currentBooking.loadingPlaces.find((lp: any) => lp.loadingType === 'UNLOADING');

    if (!loadingPlace || !unloadingPlace) {
      return null;
    }

    const loadingCity = loadingPlace.address?.city || getCountryDisplay(loadingPlace.address?.country);
    const unloadingCity = unloadingPlace.address?.city || getCountryDisplay(unloadingPlace.address?.country);

    // Get waypoints: current position, loading, unloading
    const waypoints = [];

    // Current position (always first, active/green)
    if (vehicle.currentPosition) {
      const currentCity = vehicle.currentPosition.city || getCountryDisplay(vehicle.currentPosition.country);
      waypoints.push({
        city: currentCity,
        postalCode: vehicle.currentPosition.postalCode,
        isActive: true,
      });
    }

    // Loading place
    waypoints.push({
      city: loadingCity,
      postalCode: loadingPlace.address?.postalCode || '',
      isActive: false,
    });

    // Unloading place
    waypoints.push({
      city: unloadingCity,
      postalCode: unloadingPlace.address?.postalCode || '',
      isActive: false,
    });

    return {
      route: `${loadingCity} - ${unloadingCity}`,
      waypoints,
    };
  }, [vehicle]);

  return (
    <Box
      onClick={() => onClick(vehicle)}
      sx={{
        backgroundColor: 'background.white',
        borderRadius: 2,
        p: { xs: 2.5, sm: 3 },
        minHeight: { xs: 180, sm: 200 },
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: '1px solid',
        borderColor: 'border.default',
        '&:hover': {
          borderColor: 'border.medium',
          backgroundColor: 'background.lightGrey',
        },
      }}
    >
      <Box sx={{
        display: 'flex',
        gap: 3,
        alignItems: 'flex-start'
      }}>
        {/* Left Section */}
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5
        }}>
          {/* Status */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: statusColor,
              flexShrink: 0
            }} />
            <Typography sx={{
              fontSize: (theme: any) => theme.typography.fontSizes.body,
              fontWeight: 500,
              color: 'text.dark'
            }}>
              {statusLabel}
            </Typography>
          </Box>

          {/* Truck Plates */}
          <Box>
            <Typography sx={{
              fontSize: (theme: any) => theme.typography.fontSizes.heading2,
              fontWeight: 600,
              color: 'text.dark',
              lineHeight: 1.2,
              mb: 0.5
            }}>
              {vehicle.truckPlate || 'N/A'}
            </Typography>
            {vehicle.trailerPlate && (
              <Typography sx={{
                fontSize: (theme: any) => theme.typography.fontSizes.body,
                fontWeight: 400,
                color: 'text.grey',
                lineHeight: 1.2
              }}>
                {vehicle.trailerPlate}
              </Typography>
            )}
          </Box>

          {/* Distance */}
          <Box>
            <Typography sx={{
              fontSize: (theme: any) => theme.typography.fontSizes.small,
              fontWeight: 500,
              color: 'text.grey',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 0.25
            }}>
              {t('Distance')}
            </Typography>
            <Typography sx={{
              fontSize: (theme: any) => theme.typography.fontSizes.body,
              fontWeight: 500,
              color: 'text.dark'
            }}>
              {distance}
            </Typography>
          </Box>

          {/* Estimated Time / Empty Time */}
          <Box>
            <Typography sx={{
              fontSize: (theme: any) => theme.typography.fontSizes.small,
              fontWeight: 500,
              color: 'text.grey',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 0.25
            }}>
              {timeInfo.label}
            </Typography>
            {timeInfo.isPast ? (
              <Box sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                border: (theme: any) => `1px solid ${theme.palette.background.orange}`,
                backgroundColor: (theme: any) => theme.palette.background.orangeMedium
              }}>
                <Typography sx={{
                  fontSize: (theme: any) => theme.typography.fontSizes.body,
                  fontWeight: 500,
                  color: (theme: any) => theme.palette.background.orange,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {timeInfo.value}
                </Typography>
              </Box>
            ) : (
              <Typography sx={{
                fontSize: (theme: any) => theme.typography.fontSizes.body,
                fontWeight: 500,
                color: 'text.dark',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {timeInfo.value}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right Section - Route Visualization */}
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          {routeInfo ? (
            <>
              {/* Route Header */}
              <Typography sx={{
                fontSize: (theme: any) => theme.typography.fontSizes.body,
                fontWeight: 700,
                color: 'text.dark',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {routeInfo.route}
              </Typography>

              {/* Waypoints */}
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}>
                {routeInfo.waypoints.map((waypoint, index) => (
                  <Box key={index} sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1
                  }}>
                    {/* Dot and Line */}
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      flexShrink: 0
                    }}>
                      <Box sx={{
                        width: waypoint.isActive ? 10 : 8,
                        height: waypoint.isActive ? 10 : 8,
                        borderRadius: '50%',
                        backgroundColor: waypoint.isActive ? '#10b981' : '#9E9E9E',
                        flexShrink: 0
                      }} />
                      {index < routeInfo.waypoints.length - 1 && (
                        <Box sx={{
                          width: 2,
                          height: 24,
                          backgroundColor: waypoint.isActive ? '#10b981' : '#e5e7eb',
                          mt: 0.25,
                          flexShrink: 0
                        }} />
                      )}
                    </Box>

                    {/* City and Postal Code */}
                    <Typography sx={{
                      fontSize: (theme: any) => theme.typography.fontSizes.body,
                      fontWeight: waypoint.isActive ? 600 : 400,
                      color: waypoint.isActive ? 'text.dark' : 'text.grey',
                      pt: 0.125,
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                      minWidth: 0
                    }}>
                      {waypoint.postalCode} {waypoint.city}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            <Typography sx={{
              fontSize: (theme: any) => theme.typography.fontSizes.body,
              color: 'text.grey',
              fontStyle: 'italic'
            }}>
              {t('No route information')}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default VehicleCard2;

